import { InputBox } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SearchParams } from '../../../api/constants';
import ContinueButton from '../../../components/continueButton/ContinueButton';
import { ErrorCode } from '../../../errorHandler/constants';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import type { AppError } from '../../../errorHandler/errors';
import { STEPS } from '../../constants';
import { useFindIdpConfigSAML, useSaveIdpConfigSAML } from '../../hooks/useIdpConfigSAML';
import './ExternalIdpGroups.less';

const FORM_FIELDS = {
  SAML_ATTRIBUTE_NAME: 'samlAttributeName',
  SAML_ATTRIBUTE_VALUE: 'samlAttributeValue',
};

const ExternalIdpGroups = ({
  metadataId,
  didVerifyDomain,
}: {
  metadataId: string;
  didVerifyDomain: boolean;
}) => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get(SearchParams.TENANT_ID);
  const { t } = useTranslation();
  const TRANSLATION_ROOT = `setup.${STEPS.EXTERNAL_IDP_INFO}`;
  const { emitError, clearErrorNotifications, emitSuccess } = useErrorContext();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const { samlConfig, error, isLoading } = useFindIdpConfigSAML(metadataId, tenantId || '');
  const saveIdpConfigSAML = useSaveIdpConfigSAML(metadataId, samlConfig?.idpConfigId, true);

  if (error && (error as AppError).code !== ErrorCode.SECURITY_GROUPS_IDP_ERROR) {
    emitError(error as AppError);
  }

  useEffect(() => {
    if (samlConfig) {
      clearErrorNotifications();
      form.setFieldsValue({
        [FORM_FIELDS.SAML_ATTRIBUTE_NAME]: samlConfig.samlGroupAttrName,
        [FORM_FIELDS.SAML_ATTRIBUTE_VALUE]: samlConfig.samlGroupAttrValues.join(', '),
      });
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [samlConfig, clearErrorNotifications, form]);

  useEffect(() => {
    if (didVerifyDomain) {
      emitSuccess(t(`${TRANSLATION_ROOT}.groups.domain-verified`));
    }
  }, [didVerifyDomain, emitSuccess, t, TRANSLATION_ROOT]);

  const handleValuesChange = () => {
    const samlAttributeName = form.getFieldValue(FORM_FIELDS.SAML_ATTRIBUTE_NAME)?.trim();
    const samlAttributeValue = form.getFieldValue(FORM_FIELDS.SAML_ATTRIBUTE_VALUE)?.trim();
    setIsButtonDisabled(!samlAttributeName || !samlAttributeValue);
  };

  return (
    <div className="external-idp-groups">
      <p className="external-idp-groups-label">{t(`${TRANSLATION_ROOT}.groups.label`)}</p>
      <Form
        className="external-idp-groups-form"
        form={form}
        onFinish={values => saveIdpConfigSAML.mutate(values)}
        onValuesChange={handleValuesChange}
      >
        {/*SAML Attribute Name */}
        <Form.Item name={FORM_FIELDS.SAML_ATTRIBUTE_NAME} rules={[{ required: true, message: '' }]}>
          <InputBox
            label={t(`${TRANSLATION_ROOT}.groups.saml-attribute-name`)}
            size="large"
            data-testid="saml-attribute-name"
            className={'input'}
          />
        </Form.Item>
        {/*SAML Attribute Value */}
        <Form.Item
          name={FORM_FIELDS.SAML_ATTRIBUTE_VALUE}
          rules={[{ required: true, message: '' }]}
        >
          <InputBox
            label={t(`${TRANSLATION_ROOT}.groups.saml-attribute-value`)}
            size="large"
            data-testid="saml-attribute-value"
            className={'input'}
          />
        </Form.Item>
        <Form.Item>
          <ContinueButton
            htmlType={'submit'}
            disabled={isLoading || isButtonDisabled || saveIdpConfigSAML.isPending}
            validateDirty={[FORM_FIELDS.SAML_ATTRIBUTE_NAME, FORM_FIELDS.SAML_ATTRIBUTE_VALUE]}
          >
            {t('common.continue-button')}
          </ContinueButton>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ExternalIdpGroups;
